/**
 * Location service client (parent-side).
 */
import { api } from './client';

export interface FamilyPosition {
  child_id: string;
  child_name: string | null;
  color: string | null;
  lat: number;
  lng: number;
  accuracy_m: number | null;
  speed_mps: number | null;
  bearing_deg: number | null;
  battery_pct: number | null;
  activity: string | null;
  reported_at: string;
  freshness_seconds: number;
}

export interface Geofence {
  id: string;
  name: string;
  center_lat: number;
  center_lng: number;
  radius_m: number;
  icon: string | null;
  alert_on: string[];
  created_at: string;
}

export interface HistoryPoint {
  lat: number;
  lng: number;
  reported_at: string;
  speed_mps: number | null;
  activity: string | null;
}

export interface ChildHistory {
  child_id: string;
  hours: number;
  points: HistoryPoint[];
}

export interface FamilyCurrentResponse {
  positions: FamilyPosition[];
  geofences: Geofence[];
}

export interface SosEvent {
  id: string;
  child_id: string;
  triggered_at: string;
  lat: number | null;
  lng: number | null;
  acknowledged_at: string | null;
  acknowledged_by: string | null;
  resolved_at: string | null;
  resolved_by: string | null;
  resolution_note: string | null;
  escalated_at: string | null;
  escalated_by: string | null;
  escalation_note: string | null;
  triggered_by: 'child' | 'parent';
  triggered_by_user_id: string | null;
}

export interface SosLiveSnapshot {
  sos: SosEvent;
  child_id: string;
  position: FamilyPosition | null;
  escalated: boolean;
}

export type PlaceType = 'home' | 'school' | 'place';

export interface SuggestedPlace {
  id: string;
  child_id: string;
  center_lat: number;
  center_lng: number;
  radius_m: number;
  place_type: PlaceType;
  dwell_minutes: number;
  distinct_days: number;
  last_seen_at: string;
  status: 'pending' | 'accepted' | 'dismissed';
  accepted_geofence_id: string | null;
  created_at: string;
}

export interface SafeWalk {
  id: string;
  family_id: string;
  child_id: string;
  status: 'active' | 'arrived' | 'finished' | 'expired';
  label: string | null;
  origin_lat: number;
  origin_lng: number;
  dest_lat: number;
  dest_lng: number;
  dest_name: string | null;
  route_coords: [number, number][] | null;
  route_distance_m: number | null;
  route_duration_s: number | null;
  started_at: string;
  last_lat: number | null;
  last_lng: number | null;
  last_position_at: string | null;
  deviation_alerted_at: string | null;
  stop_alerted_at: string | null;
  arrived_at: string | null;
  ended_at: string | null;
}

export interface SafeWalkStartInput {
  child_id: string;
  dest_lat: number;
  dest_lng: number;
  dest_name?: string;
  label?: string;
  origin_lat?: number;
  origin_lng?: number;
}

export interface DeviceHealth {
  child_id: string;
  battery_pct: number | null;
  is_charging: boolean | null;
  last_seen_at: string | null;
  freshness_seconds: number | null;
  state: 'healthy' | 'low_battery' | 'stale' | 'offline';
  message: string;
}

export const locationsApi = {
  familyCurrent: () => api<FamilyCurrentResponse>('/v1/locations/family/current'),
  deviceHealth: () => api<DeviceHealth[]>('/v1/locations/family/device-health'),
  childHistory: (childId: string, hours = 24) =>
    api<ChildHistory>(`/v1/locations/children/${childId}/history?hours=${hours}`),

  startSafeWalk: (input: SafeWalkStartInput) =>
    api<SafeWalk>('/v1/locations/safe-walk', { method: 'POST', body: input }),
  activeSafeWalks: () => api<SafeWalk[]>('/v1/locations/safe-walk/active'),
  finishSafeWalk: (id: string) =>
    api<SafeWalk>(`/v1/locations/safe-walk/${id}/finish`, { method: 'POST' }),

  listSuggestedPlaces: () =>
    api<SuggestedPlace[]>('/v1/locations/suggested-places'),
  recomputeSuggestedPlaces: (days = 14) =>
    api<SuggestedPlace[]>(`/v1/locations/suggested-places/recompute?days=${days}`, {
      method: 'POST',
    }),
  acceptSuggestedPlace: (id: string, payload?: { name?: string; icon?: string }) =>
    api<Geofence>(`/v1/locations/suggested-places/${id}/accept`, {
      method: 'POST',
      body: payload ?? {},
    }),
  dismissSuggestedPlace: (id: string) =>
    api<SuggestedPlace>(`/v1/locations/suggested-places/${id}/dismiss`, { method: 'POST' }),

  createGeofence: (input: {
    name: string;
    center_lat: number;
    center_lng: number;
    radius_m: number;
    icon?: string;
    child_id?: string;
    alert_on?: string[];
  }) => api<Geofence>('/v1/locations/geofences', { method: 'POST', body: input }),
  deleteGeofence: (id: string) =>
    api<void>(`/v1/locations/geofences/${id}`, { method: 'DELETE' }),
  updateGeofence: (
    id: string,
    input: Partial<{
      name: string;
      center_lat: number;
      center_lng: number;
      radius_m: number;
      icon: string;
      alert_on: string[];
    }>,
  ) =>
    api<Geofence>(`/v1/locations/geofences/${id}`, {
      method: 'PATCH',
      body: input,
    }),

  listSos: () => api<SosEvent[]>('/v1/locations/sos'),
  activeSos: () => api<SosLiveSnapshot[]>('/v1/locations/sos/active'),
  ackSos: (sosId: string) =>
    api<SosEvent>(`/v1/locations/sos/${sosId}/ack`, { method: 'POST' }),
  resolveSos: (sosId: string, note?: string) =>
    api<SosEvent>(`/v1/locations/sos/${sosId}/resolve`, {
      method: 'POST',
      body: { note: note ?? null },
    }),
  notifyCircle: (sosId: string, note?: string) =>
    api<SosEvent>(`/v1/locations/sos/${sosId}/notify-circle`, {
      method: 'POST',
      body: { note: note ?? null },
    }),
  parentPanic: (childId: string, note?: string) =>
    api<SosEvent>('/v1/locations/panic', {
      method: 'POST',
      body: { child_id: childId, note: note ?? null },
    }),

  // Dev-only — seed one-shot fake positions.
  devSeed: (
    children: Array<{ child_id: string; name: string; color: string; center_lat: number; center_lng: number }>,
  ) => api<{ seeded: number }>('/v1/locations/_dev/seed', { method: 'POST', body: { children } }),

  // Dev-only — start/stop background walker tasks (smooth realtime demo).
  devStartWalks: (payload: {
    children: Array<{
      child_id: string;
      center_lat: number;
      center_lng: number;
      speed_mps?: number;
    }>;
    duration_seconds?: number;
    step_interval_seconds?: number;
  }) =>
    api<
      Array<{
        walk_id: string;
        child_id: string;
        started_at: string;
        expires_at: string;
        speed_mps: number;
      }>
    >('/v1/locations/_dev/walks', { method: 'POST', body: payload }),

  devStopWalks: () => api<{ stopped: number }>('/v1/locations/_dev/walks', { method: 'DELETE' }),

  devListWalks: () =>
    api<
      Array<{
        walk_id: string;
        child_id: string;
        started_at: string;
        expires_at: string;
        speed_mps: number;
      }>
    >('/v1/locations/_dev/walks'),
};
